import React, { useEffect, useState, useRef } from "react";
import { db } from "./firebase";
import { ref, onValue, update, runTransaction } from "firebase/database";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";

const allStatuses = ["Recevé", "En Préparation", "Sortir en Livraison", "Livré", "Terminé", "Annulé"];

// إعادة المخزون
const restockProducts = async (distributorId, orderId) => {
  try {
    const productsRef = ref(db, `distributors/${distributorId}/orders/${orderId}/products`);
    return new Promise((resolve) => {
      onValue(productsRef, async (snap) => {
        const products = snap.val() || {};
        const promises = Object.keys(products).map(productKey => {
          const product = products[productKey];
          const productId = product.id || product.productId;
          const quantity = product.quantity || 0;
          if (!productId || quantity <= 0) return null;
          const stockRef = ref(db, `distributors/${distributorId}/products/${productId}/quantity`);
          return runTransaction(stockRef, (currentQty) => (currentQty || 0) + quantity);
        });
        await Promise.all(promises);
        resolve();
      }, { onlyOnce: true });
    });
  } catch (err) {
    console.error("خطأ في إعادة المخزون:", err);
    throw err;
  }
};

export default function OrdersPage({ uid }) {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const filter = query.get("filter"); // "new" أو null

  const containerRef = useRef();
  const PAGE_SIZE = 10;

  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [vendeursMap, setVendeursMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [restFilter, setRestFilter] = useState(false);

  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [orderTotal, setOrderTotal] = useState(0);
  const [paidAmount, setPaidAmount] = useState("");

  const remainingAmountCalc = Math.max(orderTotal - (Number(paidAmount) || 0), 0);

  // جلب البائعين
  useEffect(() => {
    const vendeursRef = ref(db, `distributors/${uid}/vendeurs`);
    const unsub = onValue(vendeursRef, snap => {
      const val = snap.val() || {};
      const map = {};
      Object.keys(val).forEach(key => {
        map[key] = `${val[key].name} ${val[key].lastName || ""}`.trim();
      });
      setVendeursMap(map);
    });
    return () => unsub();
  }, [uid]);

  // تحميل كل الطلبات
  useEffect(() => {
    if (!uid) return;
    setLoading(true);

    const ordersRef = ref(db, `distributors/${uid}/orders`);
    const unsub = onValue(ordersRef, snap => {
      const val = snap.val() || {};
      let list = Object.keys(val).map(key => ({
        id: key,
        ...val[key],
        createdAt: Number(val[key].createdAt) || 0
      }));

      // ترتيب الأحدث أولًا
      list.sort((a, b) => b.createdAt - a.createdAt);

      setAllOrders(list);
      setLoading(false);
    });

    return () => unsub();
  }, [uid]);

  // فلترة + تحديث عرض الطلبات
  useEffect(() => {
    if (!allOrders) return;

    let filtered = [...allOrders];

    if (filter === "new") filtered = filtered.filter(o => o.status === "Recevé");
    if (statusFilter !== "all") filtered = filtered.filter(o => o.status === statusFilter);
    if (restFilter) filtered = filtered.filter(o => (o.resteAmount || 0) > 0);
    if (startDate) {
      const start = new Date(startDate).getTime();
      filtered = filtered.filter(o => o.createdAt >= start);
    }
    if (endDate) {
      const end = new Date(endDate).getTime();
      filtered = filtered.filter(o => o.createdAt <= end);
    }

    setNewOrdersCount(filtered.length);
    setOrders(filtered.slice(0, PAGE_SIZE));
    setHasMore(filtered.length > PAGE_SIZE);
  }, [allOrders, statusFilter, startDate, endDate, restFilter, filter]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current || loading || !hasMore) return;
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        loadMoreOrders();
      }
    };
    const container = containerRef.current || window;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [orders, hasMore, loading]);

  const loadMoreOrders = () => {
    if (!allOrders || loading) return;
    const filtered = getFilteredOrders();
    const nextOrders = filtered.slice(orders.length, orders.length + PAGE_SIZE);
    setOrders(prev => [...prev, ...nextOrders]);
    setHasMore(orders.length + nextOrders.length < filtered.length);
  };

  const getFilteredOrders = () => {
    let filtered = [...allOrders];
    if (filter === "new") filtered = filtered.filter(o => o.status === "Recevé");
    if (statusFilter !== "all") filtered = filtered.filter(o => o.status === statusFilter);
    if (restFilter) filtered = filtered.filter(o => (o.resteAmount || 0) > 0);
    if (startDate) {
      const start = new Date(startDate).getTime();
      filtered = filtered.filter(o => o.createdAt >= start);
    }
    if (endDate) {
      const end = new Date(endDate).getTime();
      filtered = filtered.filter(o => o.createdAt <= end);
    }
    return filtered;
  };

  // تحديث حالة الطلب
  const updateOrderStatus = async (uid, orderId, newStatus) => {
    try {
      if (newStatus === "Terminé") {
        const order = orders.find(o => o.id === orderId);
        if (order) setOrderTotal(order.finalPrice ?? order.totalPrice ?? 0);
        setSelectedOrderId(orderId);
        setShowPaymentDialog(true);
        return;
      }

      if (newStatus === "Annulé") {
        if (window.confirm("هل تريد إلغاء الطلب؟ سيتم إعادة المخزون تلقائياً.")) {
          await restockProducts(uid, orderId);
        }
      }

      await update(ref(db, `distributors/${uid}/orders/${orderId}`), { status: newStatus });
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    } catch (err) {
      console.error(err);
    }
  };

  // تأكيد الدفع
  const confirmPayment = async () => {
    try {
      const order = orders.find(o => o.id === selectedOrderId);
      if (!order) return;

      const newPaid = (order.paidAmount || 0) + Number(paidAmount || 0);
      const total = order.finalPrice ?? order.totalPrice ?? 0;
      const newReste = Math.max(total - newPaid, 0);

      await update(ref(db, `distributors/${uid}/orders/${selectedOrderId}`), {
        paidAmount: newPaid,
        resteAmount: newReste,
        status: "Terminé"
      });

      setOrders(prev => prev.map(o => o.id === selectedOrderId ? {
        ...o,
        paidAmount: newPaid,
        resteAmount: newReste,
        status: "Terminé"
      } : o));

      setShowPaymentDialog(false);
      setPaidAmount("");
    } catch (err) {
      console.error(err);
    }
  };

  const getClientName = o => o.nameClientAdd || o.tradeName || "-";
  const getClientPhone = o => o.telClientAdd || o.phone || "-";
  const getClientAddress = o => o.adresseClientAdd || o.adresse || "-";

  // أنماط
  const styles = {
    table: { width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,.05)", fontFamily: "'Inter', roboto" },
    th: { background: "#f3f4f6", padding: 16, textAlign: "left", fontWeight: 600, fontSize: 14, color: "#111827", textTransform: "uppercase" },
    td: { padding: 16, borderBottom: "1px solid #e5e7eb", fontSize: 14, color: "#374151", transition: "background 0.2s" },
    trHover: { cursor: "default", transition: "background 0.2s" },
    input: { padding: "8px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, width: "100%" },
    detailsButton: { background: "#3b82f6", color: "white", border: "none", padding: "8px 12px", borderRadius: "6px", cursor: "pointer" },
    shimmerRow: { height: 50, marginBottom: 10, borderRadius: 6, background: "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }
  };

  const shimmerKeyframes = `
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }`;

  return (
    <div style={{ padding: 20, height: "80vh", overflowY: "auto" }} ref={containerRef}>
      <style>{shimmerKeyframes}</style>
      <h2>Mes Commandes</h2>

      {/* Filter Bar */}
      <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "nowrap", overflowX: "auto" }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={styles.input}>
          <option value="all">Tous les statuts</option>
          {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={styles.input} />
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={styles.input} />
        <label style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <input type="checkbox" checked={restFilter} onChange={e => setRestFilter(e.target.checked)} /> Commandes avec reste
        </label>
        <button onClick={() => { setStatusFilter("all"); setStartDate(""); setEndDate(""); setRestFilter(false); }} style={{ background: "#3b82f6", color: "white", border: "none", padding: "8px 12px", borderRadius: 6, cursor: "pointer" }}>Reset</button>
      </div>

      {/* Orders Table */}
      {newOrdersCount === 0 && !loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh", fontSize: 20, color: "#555" }}>Aucune commande trouvée</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>N° commande</th>
              <th style={styles.th}>Vendeur</th>
              <th style={styles.th}>Client</th>
              <th style={styles.th}>Téléphone</th>
              <th style={styles.th}>Adresse</th>
              <th style={styles.th}>Total</th>
              <th style={styles.th}>Payé</th>
              <th style={styles.th}>Reste</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <tr key={o.id} style={{ ...styles.trHover, background: o.status === "Annulé" ? "#fee" : "white", cursor: "pointer" }} onClick={() => navigate(`/order/${uid}/${o.id}`)}>
                <td style={styles.td}>{o.orderNumber}</td>
                <td style={styles.td}>{vendeursMap[o.vendeurId] || "—"}</td>
                <td style={styles.td}>{getClientName(o)}</td>
                <td style={styles.td}>{getClientPhone(o)}</td>
                <td style={styles.td}>{getClientAddress(o)}</td>
                <td style={styles.td}>{o.finalPrice ?? o.totalPrice} DA</td>
                <td style={{ ...styles.td, color: "#16a34a", fontWeight: "bold" }}>{o.paidAmount ?? 0} DA</td>
                <td style={{ ...styles.td, color: o.resteAmount > 0 ? "red" : "#16a34a", fontWeight: "bold", cursor: o.resteAmount > 0 ? "pointer" : "default" }} onClick={e => { e.stopPropagation(); if (o.resteAmount > 0) { setSelectedOrderId(o.id); setOrderTotal(o.resteAmount); setPaidAmount(""); setShowPaymentDialog(true); } }}>{o.resteAmount ?? 0} DA</td>
                <td style={styles.td}>
                  {o.status === "Annulé" ? <div style={{ padding: "8px 12px", borderRadius: 8, background: "#fee", color: "#dc2626", fontWeight: "bold", border: "1px solid #fecaca", textAlign: "center" }}>Annulé</div> :
                    <select value={o.status} onChange={e => updateOrderStatus(uid, o.id, e.target.value)} onClick={e => e.stopPropagation()} style={styles.input} disabled={o.status === "Terminé"}>
                      {allStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>}
                </td>
                <td style={styles.td}>{o.createdAt ? new Date(o.createdAt).toLocaleString() : "-"}</td>
                <td style={styles.td}><button onClick={() => navigate(`/order/${uid}/${o.id}`)} style={styles.detailsButton}>Détails</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Payment Dialog */}
      {showPaymentDialog && (
        <div style={{ position: "fixed", top:0, left:0, right:0, bottom:0, background:"rgba(0,0,0,0.4)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"white", padding:30, borderRadius:12, width:350 }}>
            <h3>Paiement</h3>
            <div style={{marginBottom:10}}><b>Montant total:</b> {orderTotal} DA</div>
            <div style={{marginBottom:10}}>
              <label>Montant payé</label>
              <input type="number" value={paidAmount} onChange={e=>setPaidAmount(e.target.value)} style={styles.input} />
            </div>
            <div style={{marginBottom:20}}><b>Solde restant:</b> {remainingAmountCalc} DA</div>
            <div style={{display:"flex", gap:10}}>
              <button onClick={()=>{ confirmPayment(); }} style={{ background:"#16a34a", color:"white", border:"none", padding:"10px 15px", borderRadius:6, cursor:"pointer" }}>Valider</button>
              <button onClick={()=>setShowPaymentDialog(false)} style={{ background:"#ef4444", color:"white", border:"none", padding:"10px 15px", borderRadius:6, cursor:"pointer" }}>Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}